import classNames from 'classnames'
import React from 'react'

import { Button, type ButtonProps } from '../../../components/button/button.js'
import { Card } from '../../../components/card/card.js'
import { Textfield } from '../../../components/forms/textfield/textfield.js'
import { Toggle, ToggleProps } from '../../../components/forms/toggle/toggle.js'
import { GridColumn } from '../../../components/grid/grid-column.js'
import { Grid } from '../../../components/grid/grid.js'
import { useCurrentBreakPoint } from '../../../components/helper/hooks/use-current-breakpoint.js'
import { Menu, type MenuProps } from '../../../components/menu/menu.js'
import { Modal, ModalProps, ModalProvider, ModalTrigger } from '../../../components/modal/modal.js'
import { PageTitle, PageTitleProps } from '../../../components/page-title/page-title.js'
import { Separator } from '../../../components/separator/separator.js'

export interface ViewAccountProps {
    title: PageTitleProps
    menu?: MenuProps
    blocks: ViewAccountBlockProps[]
}

export interface ViewAccountBlockProps {
    title?: string
    items?: ViewAccountItemProps[]
    button?: ButtonProps
    toggles?: ViewAccountTogglesProps[]
    card?: ViewAccountCardProps
}

export interface ViewAccountCardProps {
    title: string
    description?: string
    linkText?: string
    link?: string
}

export interface ViewAccountTogglesProps {
    title: string
    toggle: ToggleProps
    modal?: {
        trigger: string
        modal: ModalProps
    }
}

export interface ViewAccountItemProps {
    key: string
    value: string
    isAlerted?: boolean
    isEditable?: boolean
    onEdit?: (value: string) => void
    error?: string
}

export const ViewAccount = (props: ViewAccountProps): JSX.Element => {
    const bp = useCurrentBreakPoint()
    const isMobile = bp === 'xs' || bp === 'sm'

    return (
        <Grid>
            <GridColumn>
                <PageTitle {...props.title} alwaysButton />
            </GridColumn>
            <GridColumn>
                <div className="account">
                    {props.menu && !isMobile && (
                        <>
                            <Menu className="account__menu" {...props.menu} />
                            <div className="account__separator"></div>
                        </>
                    )}
                    <div className="account__blocks">
                        {props.blocks.map((block: ViewAccountBlockProps, i: number) => renderBlock(block, i, isMobile))}
                    </div>
                </div>
            </GridColumn>
        </Grid>
    )
}

export const renderBlock = (block: ViewAccountBlockProps, i: number, isMobile: boolean): JSX.Element => {
    if (block.card) {
        return (
            <Card hasContent padding="medium" variant="ternary" key={'block-' + i}>
                <div className="text">
                    <h3 className="text-small bold">{block.card.title}</h3>
                    {block.card.description && <p className="text-small">{block.card.description}</p>}
                    {block.card.link && (
                        <>
                            <Separator invisible variant="tiny" />
                            <a href={block.card.link}>{block.card.linkText}</a>
                        </>
                    )}
                    {block.button && <Button className="account__button" {...block.button} />}
                </div>
            </Card>
        )
    }

    return (
        <div className="account__sticky-header" key={i}>
            <Grid>
                {block.title && (
                    <GridColumn>
                        <h2 className="text-large bold">{block.title}</h2>
                    </GridColumn>
                )}

                {block.toggles?.map((checkbox: ViewAccountTogglesProps, index: number) => (
                    <GridColumn width={['lg-6']} key={`toggle-${i}-${index}`}>
                        <Grid>
                            <GridColumn>
                                <h3 className="text-small bold">{checkbox.title}</h3>
                            </GridColumn>
                            <GridColumn>
                                <Toggle className="h-inline h-mr-8" {...checkbox.toggle} />
                                {checkbox.modal && (
                                    <ModalProvider>
                                        <ModalTrigger>
                                            <Button
                                                className="button--modal-trigger h-inline"
                                                text={checkbox.modal.trigger}
                                                style="link"
                                            />
                                        </ModalTrigger>
                                        <Modal {...checkbox.modal.modal} />
                                    </ModalProvider>
                                )}
                            </GridColumn>
                        </Grid>
                    </GridColumn>
                ))}

                {block.items?.map((item: ViewAccountItemProps, index: number) => (
                    <GridColumn width={['sm-6']} key={`${i}-${index}`}>
                        <p className="text-small text-light">{item.key}</p>
                        <Separator invisible={true} variant="tiny" />
                        {item.isEditable ? (
                            <Textfield
                                value={item.value}
                                onChange={(value) => item.onEdit && item.onEdit(value)}
                                error={item.error}
                                id={item.key}
                                name={item.key}
                            />
                        ) : (
                            <p className={classNames('text-large', item.isAlerted && 'account__item--alerted')}>
                                {item.value}
                            </p>
                        )}
                    </GridColumn>
                ))}

                {block.button && (
                    <GridColumn width={['xs-12']}>
                        <Button className="account__button" {...block.button} />
                    </GridColumn>
                )}
            </Grid>
            <Separator variant={isMobile ? undefined : 'large'} />
        </div>
    )
}
